import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";
import { Check, CreditCard, Sparkles, Wallet } from "lucide-react";

const featureSets = {
  starter: ["20 crédits / mois", "Kling Turbo (2 crédits)", "Formats 16:9 & 9:16", "Téléchargement HD"],
  pro: ["80 crédits / mois", "Kling Turbo + Veo 3.1 Fast", "Priorité de file d'attente", "Usage commercial"],
  studio: ["250 crédits / mois", "Tous les modèles", "Support prioritaire", "Usage commercial étendu"],
};

const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState(null);
  const [config, setConfig] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const startStripeCheckout = async (planId) => {
    if (!user) return navigate("/inscription");
    setLoadingPlan(planId);
    try {
      const lookupKey = `${planId}_${annual ? "yearly" : "monthly"}`;
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(getErrorMessage(e));
      setLoadingPlan(null);
    }
  };

  const startRevolutCheckout = async (planId) => {
    if (!user) return navigate("/inscription");
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/revolut/order", { plan: planId, period: "yearly", origin_url: window.location.origin });
      const script = document.createElement("script");
      script.src = "https://unpkg.com/@revolut/checkout/umd/revolut-checkout.js";
      script.onload = () => {
        window.RevolutCheckout.payments({ publicToken: config.revolut_public_key, mode: "prod", locale: "fr" }).then((rc) => {
          rc.revolutPay.mountTo(document.getElementById("revolut-pay-target"), {
            currency: "EUR",
            totalAmount: data.amount,
            createOrder: async () => ({ publicId: data.token }),
            redirectUrls: {
              success: `${window.location.origin}/payment/success`,
              failure: `${window.location.origin}/payment/cancel`,
              cancel: `${window.location.origin}/payment/cancel`,
            },
          });
        });
      };
      document.body.appendChild(script);
      setLoadingPlan(null);
    } catch (e) {
      toast.error(getErrorMessage(e));
      setLoadingPlan(null);
    }
  };

  const euro = (cents) => (cents / 100).toFixed(2).replace(".", ",");

  return (
    <div className="noise relative min-h-[80vh]">
      <div className="absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-[#8B5CF6]/20 blur-[130px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center">
          <h1 className="text-3xl sm:text-5xl font-black" data-testid="pricing-title">
            Des tarifs <span className="text-gradient">simples et honnêtes</span>
          </h1>
          <p className="mt-4 text-muted-foreground" data-testid="pricing-subtitle">
            Paiement sécurisé par Stripe ou Revolut Pay. Sans engagement.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-border bg-card px-5 py-2.5" data-testid="pricing-billing-toggle">
            <span className={!annual ? "text-sm font-medium" : "text-sm text-muted-foreground"}>Mensuel</span>
            <Switch checked={annual} onCheckedChange={setAnnual} data-testid="pricing-annual-switch" />
            <span className={annual ? "text-sm font-medium" : "text-sm text-muted-foreground"}>
              Annuel <span className="text-[#EC4899] font-semibold">−2 mois</span>
            </span>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {(plans ? Object.entries(plans) : [["starter", null], ["pro", null], ["studio", null]]).map(([id, plan]) => {
            if (!plan) return <Card key={id}><CardContent className="p-6 h-72 animate-pulse bg-muted/20" /></Card>;
            const price = annual ? plan.yearly_cents : plan.monthly_cents;
            const popular = id === "pro";
            return (
              <Card key={id} className={`relative rounded-3xl ${popular ? "border-[#8B5CF6] glow-purple" : "border-border"}`} data-testid={`pricing-card-${id}`}>
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white rounded-full px-4 py-1" data-testid={`pricing-badge-popular-${id}`}>
                    <Sparkles className="inline w-3 h-3 mr-1" />Le plus choisi
                  </span>
                )}
                <CardContent className="p-7 flex flex-col h-full">
                  <h3 className="font-display text-xl font-bold">{plan.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{plan.desc}</p>
                  <div className="mt-6 flex items-baseline gap-1" data-testid={`pricing-price-${id}`}>
                    <span className="text-4xl font-black">{euro(price)} €</span>
                    <span className="text-sm text-muted-foreground">{annual ? "/an" : "/mois"}</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {featureSets[id].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 mt-0.5 text-[#8B5CF6] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`mt-7 w-full ${popular ? "bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white border-0" : ""}`}
                    variant={popular ? "default" : "outline"}
                    disabled={loadingPlan === id}
                    onClick={() => startStripeCheckout(id)}
                    data-testid={`pricing-stripe-button-${id}`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {loadingPlan === id ? "Redirection…" : `S'abonner — ${plan.name}`}
                  </Button>
                  <Button
                    className="mt-2 w-full"
                    variant="ghost"
                    disabled={loadingPlan === id}
                    onClick={() => (config?.revolut_enabled ? startRevolutCheckout(id) : toast.info("Revolut Pay arrive bientôt : ajoutez vos clés Revolut pour l'activer."))}
                    data-testid={`pricing-revolut-button-${id}`}
                  >
                    <Wallet className="w-4 h-4 mr-2" /> Payer avec Revolut
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div id="revolut-pay-target" className="mt-10 flex justify-center" data-testid="pricing-revolut-target" />

        <p className="mt-10 text-center text-sm text-muted-foreground" data-testid="pricing-methods-note">
          Stripe accepte : Visa, Mastercard, American Express, Apple&nbsp;Pay, Google&nbsp;Pay, Link, SEPA et plus.
          Les revenus sont versés sur votre compte Revolut.
        </p>
      </div>
    </div>
  );
};

export default Pricing;
